/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { performance } from "@fluidframework/common-utils";
/**
 * Helper class that executes a specified code block and writes an
 * {@link @fluidframework/common-definitions#ITelemetryPerformanceEvent} to a specified logger every time a specified
 * number of executions is reached (or when the class is disposed). The `duration` field in the telemetry event is
 * the duration of the latest execution (sample) of the specified function. See the documentation of the
 * `includeAggregateMetrics` parameter for additional details that can be included.
 */
export class SampledTelemetryHelper {
    /**
     * @param eventBase -
     * Custom properties to include in the telemetry performance event when it is written.
     * @param logger -
     * The logger to use to write the telemetry performance event.
     * @param sampleThreshold -
     * Telemetry performance events will be generated every time we hit this many executions of the code block.
     * @param includeAggregateMetrics -
     * If set to `true`, the telemetry performance event will include aggregated metrics (total duration, min duration,
     * max duration) for all the executions in between generated events.
     * @param perBucketProperties -
     * Map of strings that represent different buckets (which can be specified when calling the 'measure' method), to
     * properties which should be added to the telemetry event for that bucket. If a bucket being measured does not
     * have an entry in this map, no additional properties will be added to its telemetry events. The following keys are
     * reserved for use by this class: "duration", "count", "totalDuration", "minDuration", "maxDuration". If any of
     * them is specified as a key in one of the ITelemetryProperties objects in this map, that key-value pair will be
     * ignored.
     */
    constructor(eventBase, logger, sampleThreshold, includeAggregateMetrics = false, perBucketProperties = new Map()) {
        this.eventBase = eventBase;
        this.logger = logger;
        this.sampleThreshold = sampleThreshold;
        this.includeAggregateMetrics = includeAggregateMetrics;
        this.perBucketProperties = perBucketProperties;
        this.disposed = false;
        this.measurementsMap = new Map();
    }
    /**
     * @param codeToMeasure -
     * The code to be executed and measured.
     * @param bucket -
     * A key to track executions of the code block separately. Each different value of this parameter has a separate
     * set of executions and metrics tracked by the class. If no such distinction needs to be made, do not provide a
     * value.
     * @returns Whatever the passed-in code block returns.
     */
    measure(codeToMeasure, bucket = "") {
        var _a, _b, _c;
        const start = performance.now();
        const returnValue = codeToMeasure();
        const duration = performance.now() - start;
        let m = this.measurementsMap.get(bucket);
        if (m === undefined) {
            m = { count: 0, duration: -1 };
            this.measurementsMap.set(bucket, m);
        }
        m.count++;
        m.duration = duration;
        if (this.includeAggregateMetrics) {
            m.totalDuration = ((_a = m.totalDuration) !== null && _a !== void 0 ? _a : 0) + duration;
            m.minDuration = Math.min((_b = m.minDuration) !== null && _b !== void 0 ? _b : duration, duration);
            m.maxDuration = Math.max((_c = m.maxDuration) !== null && _c !== void 0 ? _c : 0, duration);
        }
        if (m.count >= this.sampleThreshold) {
            this.flushBucket(bucket);
        }
        return returnValue;
    }
    flushBucket(bucket) {
        const measurements = this.measurementsMap.get(bucket);
        if (measurements === undefined) {
            return;
        }
        if (measurements.count !== 0) {
            const bucketProperties = this.perBucketProperties.get(bucket);
            const telemetryEvent = Object.assign(Object.assign(Object.assign({}, this.eventBase), bucketProperties), measurements);
            this.logger.sendPerformanceEvent(telemetryEvent);
            this.measurementsMap.delete(bucket);
        }
    }
    dispose(error) {
        this.measurementsMap.forEach((_, k) => this.flushBucket(k));
    }
}
//# sourceMappingURL=sampledTelemetryHelper.js.map